import { Button, toast } from '@patchbay/ui'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import * as React from 'react'
import type { RileyFeedbackTarget, RileyRating } from './contracts'

type Rating = RileyFeedbackTarget['rating']

/**
 * The person's own saved ratings, loaded once per thread (`GET /riley/feedback/mine`), so a thumb is still pressed
 * after a reload (#417). Absent provider: every thumb starts unrated, as before.
 */
const RatingsContext = React.createContext<ReadonlyMap<string, RileyRating> | null>(null)

export function RileyRatingsProvider({
  ids,
  load,
  children,
}: {
  ids: readonly string[]
  load?: (ids: string[]) => Promise<RileyRating[]>
  children: React.ReactNode
}) {
  const [ratings, setRatings] = React.useState<ReadonlyMap<string, RileyRating>>(new Map())
  const key = [...ids].sort().join(',')
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` is the identity of `ids`
  React.useEffect(() => {
    if (!load || !ids.length) return
    let live = true
    void load([...ids])
      .then((rows) => {
        if (live) setRatings(new Map(rows.map((row) => [row.id, row])))
      })
      .catch(() => undefined) // a missed read leaves the thumbs unpressed; rating still works
    return () => {
      live = false
    }
  }, [key, load])
  return <RatingsContext.Provider value={ratings}>{children}</RatingsContext.Provider>
}

/**
 * Thumbs up/down on a Riley message, card or investigation (scope §5.8: feeds the eval set through
 * `POST /api/v1/riley/feedback`). Without a handler there is nowhere to send the rating, so nothing renders: a
 * button must work or not be there. The chosen thumb is filled, the saved rating shows on reload, a changed mind
 * is a new rating, and a thumbs-down offers an optional "what was wrong?" note.
 */
export function RileyFeedback({
  id,
  target,
  onFeedback,
}: {
  id: string
  target: RileyFeedbackTarget['target']
  onFeedback?: (feedback: RileyFeedbackTarget) => Promise<void> | void
}) {
  const saved = React.useContext(RatingsContext)?.get(id)
  const [chosen, setChosen] = React.useState<Rating | null>(null)
  const rating: Rating | null = chosen ?? saved?.rating ?? null
  const [busy, setBusy] = React.useState(false)
  const [asking, setAsking] = React.useState(false)
  const [note, setNote] = React.useState('')

  if (!onFeedback) return null

  const send = async (next: Rating, text?: string): Promise<boolean> => {
    setBusy(true)
    try {
      await onFeedback({ id, target, rating: next, ...(text ? { note: text } : {}) })
      setChosen(next)
      return true
    } catch (error) {
      toast.error(error instanceof Error ? `Feedback not saved: ${error.message}` : 'Feedback not saved')
      return false
    } finally {
      setBusy(false)
    }
  }

  const rate = async (next: Rating) => {
    if (busy || next === rating) return
    if (await send(next)) setAsking(next === 'down')
  }

  const sendNote = async () => {
    const text = note.trim()
    if (text && !(await send('down', text))) return
    setAsking(false)
    setNote('')
  }

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-0.5" data-slot="riley-feedback" data-rating={rating ?? undefined}>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Helpful"
        aria-pressed={rating === 'up'}
        disabled={busy}
        onClick={() => void rate('up')}
        className={rating === 'up' ? 'size-7 text-riley' : 'size-7'}
      >
        <ThumbsUp className={rating === 'up' ? 'fill-current' : undefined} />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Not helpful"
        aria-pressed={rating === 'down'}
        disabled={busy}
        onClick={() => void rate('down')}
        className={rating === 'down' ? 'size-7 text-destructive' : 'size-7'}
      >
        <ThumbsDown className={rating === 'down' ? 'fill-current' : undefined} />
      </Button>
      {asking ? (
        <form
          className="flex min-w-0 items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault()
            void sendNote()
          }}
        >
          <input
            aria-label="What was wrong?"
            placeholder="What was wrong? (optional)"
            value={note}
            maxLength={2000}
            onChange={(event) => setNote(event.target.value)}
            className="h-7 min-w-0 flex-1 rounded-md border border-border bg-card px-2 text-xs outline-none focus:border-riley"
          />
          <Button type="submit" size="sm" variant="ghost" disabled={busy}>
            {note.trim() ? 'Send' : 'Skip'}
          </Button>
        </form>
      ) : null}
    </div>
  )
}
